/**
 * /auth/callback — Emergent Google OAuth landing page.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Flow:
 * 1. Emergent redirects user to <origin>/auth/callback#session_id=xxx
 * 2. We read session_id from the URL fragment
 * 3. POST to /api/auth/google-callback → backend exchanges with Emergent and
 *    returns OUR app JWT + user payload (same shape as /api/auth/login).
 * 4. Store JWT via AuthContext.applyLogin(), redirect to /dashboard.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { applyLogin } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : "";

    if (!sessionId) {
      setError("Missing session_id in URL. Please retry sign-in.");
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/google-callback`, { session_id: sessionId });
        if (!data?.access_token || !data?.user) {
          throw new Error("Invalid response from server");
        }
        // Mirror /login flow.
        if (typeof applyLogin === "function") {
          applyLogin(data.user, data.access_token);
        } else {
          // Fallback: direct localStorage
          window.localStorage.setItem("alp.access_token", data.access_token);
        }
        // Strip the fragment to avoid leaking session_id in history.
        window.history.replaceState(null, "", "/auth/callback");
        const u = data.user || {};
        const next = u.must_change_password ? "/change-password"
          : (u.role === "super_admin" || u.role === "admin") ? "/master"
          : "/dashboard";
        navigate(next, { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : (e?.message || "Failed to sign in. Please retry."));
      }
    })();
  }, [navigate, applyLogin]);

  return (
    <div data-testid="auth-callback-page"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#0F0820", color: "#FFFFFF" }}>
      <div className="rounded-2xl p-8 max-w-md w-full border" style={{ backgroundColor: "#1A0E36", borderColor: "rgba(201,162,78,0.2)" }}>
        {error ? (
          <>
            <AlertTriangle className="w-7 h-7 mb-3" style={{ color: "#EF4444" }} />
            <h1 className="font-display text-2xl font-black mb-2">Sign-in failed</h1>
            <p className="text-[13.5px] mb-5" style={{ color: "#A1A1AA" }}>{error}</p>
            <Link to="/login" data-testid="auth-callback-retry"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-bold text-[13px]"
              style={{ backgroundColor: "#C9A24E", color: "#0F0820" }}>Back to sign in</Link>
          </>
        ) : (
          <>
            <Loader2 className="w-7 h-7 mb-3 animate-spin" style={{ color: "#C9A24E" }} />
            <h1 className="font-display text-2xl font-black mb-2">Signing you in…</h1>
            <p className="text-[13.5px]" style={{ color: "#A1A1AA" }}>Hold tight, we're verifying your Google session.</p>
          </>
        )}
      </div>
    </div>
  );
}
